import * as THREE from 'three';
import { EventEmitter } from '@/core/EventEmitter';
import { settings } from '@/config';
import type { TickState } from '@/types';

// Type alias (not interface) to satisfy EventEmitter's index-signature constraint.
type InteractionEvents = {
  hover: { object: THREE.Object3D; point: THREE.Vector3 };
  hoverEnd: { object: THREE.Object3D };
  click: { object: THREE.Object3D; point: THREE.Vector3 };
};

/**
 * Pointer + raycaster system (prepared). Tracks a normalized pointer, raycasts
 * against registered objects each frame, and emits hover / hoverEnd / click.
 * No UI reactions in Phase 01 — consumers wire behavior in later phases.
 * Works for both mouse and touch via Pointer Events.
 */
export class InteractionManager extends EventEmitter<InteractionEvents> {
  enabled = settings.interaction.enabled;

  private readonly raycaster = new THREE.Raycaster();
  private readonly pointer = new THREE.Vector2();
  private readonly targets: THREE.Object3D[] = [];
  private hovered: THREE.Object3D | null = null;
  private pointerActive = false;

  constructor(
    private readonly camera: THREE.Camera,
    private readonly dom: HTMLElement,
  ) {
    super();
    this.dom.addEventListener('pointermove', this.onPointerMove, { passive: true });
    this.dom.addEventListener('pointerdown', this.onPointerDown, { passive: true });
    this.dom.addEventListener('pointerleave', this.onPointerLeave, { passive: true });
  }

  setTargets(objects: THREE.Object3D[]): void {
    this.targets.length = 0;
    this.targets.push(...objects);
  }

  add(object: THREE.Object3D): void {
    this.targets.push(object);
  }

  clear(): void {
    this.targets.length = 0;
  }

  get hoveredObject(): THREE.Object3D | null {
    return this.hovered;
  }

  private updatePointer(event: PointerEvent): void {
    const rect = this.dom.getBoundingClientRect();
    this.pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    this.pointerActive = true;
  }

  private onPointerMove = (event: PointerEvent): void => this.updatePointer(event);

  private onPointerDown = (event: PointerEvent): void => {
    this.updatePointer(event);
    const hit = this.raycast();
    if (hit) this.emit('click', { object: hit.object, point: hit.point });
  };

  private onPointerLeave = (): void => {
    this.pointerActive = false;
    if (this.hovered) {
      this.emit('hoverEnd', { object: this.hovered });
      this.hovered = null;
    }
  };

  private raycast(): THREE.Intersection | null {
    if (!this.targets.length) return null;
    this.raycaster.setFromCamera(this.pointer, this.camera);
    const hits = this.raycaster.intersectObjects(this.targets, settings.interaction.recursive);
    return hits.length ? hits[0] : null;
  }

  update(_tick: TickState): void {
    if (!this.enabled || !this.pointerActive) return;
    const hit = this.raycast();
    const object = hit?.object ?? null;
    if (object === this.hovered) return;

    if (this.hovered) this.emit('hoverEnd', { object: this.hovered });
    this.hovered = object;
    if (object && hit) this.emit('hover', { object, point: hit.point });
  }

  dispose(): void {
    this.dom.removeEventListener('pointermove', this.onPointerMove);
    this.dom.removeEventListener('pointerdown', this.onPointerDown);
    this.dom.removeEventListener('pointerleave', this.onPointerLeave);
    this.clearListeners();
  }
}
